import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeMoeda,
  CampoDeSelecao,
  CampoDeTexto,
  Cartao,
  CartaoIndicador,
  EstadoVazio,
  ValorFiscal,
  ValorPercentual,
} from '../../componentes/ui';
import { MeusPedidos, invalidarPedidos } from '../../componentes/PedidosDeFechamento';
import { useCarteiraDoAssessor } from '../../hooks/useParticipacao';
import { useEmpresaCorrente } from '../../hooks/useSessao';
import { simularPassivo, type EntradaSimulacaoPassivo } from '../../servicos/escrita';
import { lerValorDigitado } from '../../servicos/formato';
import { pedirFechamento } from '../../servicos/participacao';
import { Pagina } from '../Carteira';
import { ErroDaApi } from '../ErroDaApi';
import { TabelaDeParcelas } from '../Passivo';
import { lerDefaults } from './configuracoes';
import { explicacaoDoSistema } from './sistema-de-amortizacao';

/**
 * Simulações do Passivo, para o assessor — o mesmo item do menu do admin.
 *
 * A conta é a da operação (`POST /passivo/simulacoes`, no servidor) e vale por
 * si: às vezes o assessor só quer o número, para conversar com o cliente.
 * Pedir o fechamento é o passo **opcional** de depois — escolhe o cliente da
 * carteira e manda os números como ele viu; a operação monta o contrato com o
 * fornecedor do crédito. Os pedidos ficam logo abaixo, até serem atendidos.
 *
 * O pedido leva a entrada **simulada**, não a do formulário: mexer num campo
 * depois de simular trava o pedido até simular de novo, para a operação
 * receber exatamente o que o assessor viu.
 */
export function SimulacoesPassivoDaParte(): JSX.Element {
  const empresa = useEmpresaCorrente();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => ({ valorDivida: '', ...lerDefaults() }));
  const [clienteId, setClienteId] = useState('');
  const [observacao, setObservacao] = useState('');

  const simular = useMutation({ mutationFn: simularPassivo });
  const carteira = useCarteiraDoAssessor(simular.data !== undefined);

  const pedir = useMutation({
    mutationFn: () => {
      const { parcelas, ...resumo } = simular.data ?? { parcelas: [] };
      void parcelas;
      return pedirFechamento({
        empresa: empresa ?? '',
        servico: 'PASSIVO',
        clienteId,
        simulacao: { entradas: simular.variables, resultado: resumo },
        ...(observacao.trim() === '' ? {} : { observacao: observacao.trim() }),
      });
    },
    onSuccess: () => invalidarPedidos(queryClient),
  });

  const lerEntrada = (): EntradaSimulacaoPassivo | null => {
    const valorDivida = lerValorDigitado(form.valorDivida);
    if (valorDivida === null || valorDivida <= 0) return null;
    return {
      valorDivida,
      percentualDesagio: Number(form.percentualDesagio.replace(',', '.')),
      quantidadeParcelas: Math.trunc(Number(form.quantidadeParcelas)),
      taxaJurosMensal: Number(form.taxaJurosMensal.replace(',', '.')),
      indice: form.indice,
    };
  };

  const enviar = (): void => {
    const entrada = lerEntrada();
    if (entrada === null) return;
    pedir.reset();
    simular.mutate(entrada);
  };

  const r = simular.data;
  const simulada = simular.variables;
  const atual = lerEntrada();
  const parametrosMudaram =
    r !== undefined &&
    simulada !== undefined &&
    (atual === null || JSON.stringify(atual) !== JSON.stringify(simulada));

  const novaSimulacao = (): void => {
    simular.reset();
    pedir.reset();
    setForm((f) => ({ ...f, valorDivida: '' }));
    setClienteId('');
    setObservacao('');
  };

  const clientes = carteira.data ?? [];
  const semCarteira = carteira.isSuccess && clientes.length === 0;

  return (
    <Pagina
      titulo="ICMS Passivo"
      descricao="Simule a quitação de uma dívida de ICMS com crédito de terceiro. Se o cliente aceitar, peça o fechamento à operação — é opcional."
    >
      <div className="grid gap-6 lg:grid-cols-[22rem_1fr]">
        <Cartao como="section" titulo="Parâmetros">
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              enviar();
            }}
          >
            <CampoDeMoeda
              rotulo="Dívida de ICMS"
              required
              value={form.valorDivida}
              onChange={(texto) => setForm({ ...form, valorDivida: texto })}
            />
            <Campo
              rotulo="Deságio"
              numerico
              sufixo="%"
              ajuda="Percentual de desconto sobre o crédito comprado."
              value={form.percentualDesagio}
              onChange={(e) => setForm({ ...form, percentualDesagio: e.target.value })}
            />
            <Campo
              rotulo="Parcelas"
              numerico
              value={form.quantidadeParcelas}
              onChange={(e) => setForm({ ...form, quantidadeParcelas: e.target.value })}
            />
            <Campo
              rotulo="Taxa de juros mensal"
              numerico
              sufixo="%"
              ajuda="Entre 0 e 10."
              value={form.taxaJurosMensal}
              onChange={(e) => setForm({ ...form, taxaJurosMensal: e.target.value })}
            />
            <CampoDeSelecao
              rotulo="Sistema de amortização"
              ajuda={explicacaoDoSistema(form.indice, form.taxaJurosMensal)}
              opcoes={[
                { valor: 'sac', rotulo: 'SAC' },
                { valor: 'price', rotulo: 'Price' },
              ]}
              value={form.indice}
              onChange={(e) => setForm({ ...form, indice: e.target.value as 'sac' | 'price' })}
            />

            {simular.isError && (
              <ErroDaApi titulo="A simulação não foi feita" erro={simular.error} />
            )}

            <Botao type="submit" carregando={simular.isPending}>
              {simular.isPending ? 'Simulando…' : 'Simular'}
            </Botao>
          </form>
        </Cartao>

        <div className="flex min-w-0 flex-col gap-6">
          {r === undefined ? (
            <EstadoVazio
              titulo="Nenhuma simulação ainda"
              descricao="Informe a dívida e os parâmetros e clique em Simular. Nada é gravado: é só a conta, com a mesma regra da operação."
            />
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <CartaoIndicador
                  rotulo="Dívida original"
                  valor={<ValorFiscal valor={r.valorDivida} tamanho="grande" />}
                />
                <CartaoIndicador
                  rotulo="Valor a pagar"
                  valor={<ValorFiscal valor={r.valorAPagar} tamanho="grande" />}
                  detalhe="Crédito comprado menos o deságio."
                />
                <CartaoIndicador
                  rotulo="Total parcelado"
                  valor={<ValorFiscal valor={r.valorTotal} tamanho="grande" />}
                  detalhe="Com juros, ao longo das parcelas."
                />
                <CartaoIndicador
                  rotulo="Economia"
                  valor={<ValorFiscal valor={r.economia} variante="economia" tamanho="grande" />}
                  detalhe={
                    <>
                      <ValorPercentual percentual={r.percentualEconomia} /> sobre a dívida.
                    </>
                  }
                />
              </div>
              {r.economia < 0 && (
                <Aviso tom="atencao" titulo="Esta simulação custa mais que a dívida">
                  Com os juros, o total parcelado supera a dívida. Reduza o prazo ou a taxa, ou
                  aumente o deságio.
                </Aviso>
              )}
              <TabelaDeParcelas parcelas={r.parcelas} total={r.valorTotal} />

              <Cartao
                como="section"
                titulo="Pedir fechamento à operação"
                subtitulo="Opcional — quando o cliente aceitar. A operação monta o contrato com estes números."
              >
                {pedir.isSuccess ? (
                  <div className="flex flex-col gap-3">
                    <Aviso tom="positivo" titulo="Pedido enviado à operação">
                      A operação foi avisada e monta o contrato com estes números. O pedido aparece
                      em “Meus pedidos de fechamento” até ser atendido; o contrato, em{' '}
                      <Link to="/passivo/contratos" className="underline underline-offset-2">
                        Contratos
                      </Link>
                      .
                    </Aviso>
                    <div>
                      <Botao variante="secundario" onClick={novaSimulacao}>
                        Nova simulação
                      </Botao>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <CampoDeSelecao
                      rotulo="Cliente"
                      ajuda="Da sua carteira."
                      value={clienteId}
                      onChange={(e) => setClienteId(e.target.value)}
                      disabled={semCarteira}
                      opcoes={[
                        { valor: '', rotulo: carteira.isPending ? 'Carregando…' : 'Selecione…' },
                        ...clientes.map((c) => ({ valor: c.businessId, rotulo: c.razaoSocial })),
                      ]}
                    />
                    <CampoDeTexto
                      rotulo="Observação para a operação"
                      rows={2}
                      ajuda="Opcional: prazo combinado, quem assina, o que o cliente pediu."
                      value={observacao}
                      onChange={(e) => setObservacao(e.target.value)}
                    />
                    {semCarteira && (
                      <Aviso tom="atencao" titulo="Nenhum cliente na sua carteira">
                        O pedido de fechamento é para cliente seu. Peça à operação para vincular o
                        cliente à sua assessoria.
                      </Aviso>
                    )}
                    {carteira.isError && (
                      <ErroDaApi
                        titulo="Não foi possível carregar a carteira"
                        erro={carteira.error}
                      />
                    )}
                    {parametrosMudaram && (
                      <Aviso tom="atencao" titulo="Os parâmetros mudaram">
                        Simule de novo para pedir com os números que estão no formulário.
                      </Aviso>
                    )}
                    {pedir.isError && (
                      <ErroDaApi titulo="Não foi possível pedir" erro={pedir.error} />
                    )}
                    <div>
                      <Botao
                        disabled={clienteId === '' || parametrosMudaram}
                        carregando={pedir.isPending}
                        onClick={() => pedir.mutate()}
                      >
                        Pedir fechamento
                      </Botao>
                    </div>
                  </div>
                )}
              </Cartao>
            </>
          )}
        </div>
      </div>

      <MeusPedidos servico="PASSIVO" />
    </Pagina>
  );
}
